import { useEffect, useState, type RefObject } from 'react';

/**
 * Returns `true` once the viewer's scroll element is at (or fits within) its bottom. Re-attaches
 * when the scroll element is replaced (PDF load, file type change).
 */
export const useDocViewerScrollAtBottom = (wrapperRef: RefObject<HTMLDivElement | null>) => {
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let scrollEl: HTMLElement | null = null;
    let cleanupScroll: (() => void) | null = null;

    const update = () => {
      if (!scrollEl) return;
      setIsAtBottom(scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1);
    };

    const attach = () => {
      const found = wrapper.querySelector<HTMLElement>('.hide-scrollbar');
      if (found === scrollEl) return;
      cleanupScroll?.();
      cleanupScroll = null;
      scrollEl = found;
      if (!scrollEl) {
        setIsAtBottom(false);
        return;
      }
      const el = scrollEl;
      el.addEventListener('scroll', update, { passive: true });
      const ro = new ResizeObserver(update);
      ro.observe(el);
      cleanupScroll = () => {
        el.removeEventListener('scroll', update);
        ro.disconnect();
      };
      update();
    };

    attach();
    const mo = new MutationObserver(attach);
    mo.observe(wrapper, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      cleanupScroll?.();
    };
  }, [wrapperRef]);

  return isAtBottom;
};
